import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';

export interface VersionInfo {
  version: string;
  buildId?: string;
  timestamp?: string;
}

export function useAppVersion() {
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current version from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('app_version');
    console.log('[VERSION] [DIAGNOSTIC] localStorage app_version:', stored);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        console.log('[VERSION] [DIAGNOSTIC] Parsed stored version:', parsed);
        setCurrentVersion(parsed);
      } catch (err) {
        console.error('[VERSION] Error parsing stored version:', err);
      }
    } else {
      console.log('[VERSION] [DIAGNOSTIC] No stored version found');
    }
  }, []);

  // Check for updates
  const checkForUpdates = async () => {
    try {
      setIsChecking(true);
      setError(null);

      const response = await apiClient.get('/api/version');
      const serverVersion: VersionInfo = response.data;

      console.log('[VERSION] [DIAGNOSTIC] Server returned version:', serverVersion);
      setLatestVersion(serverVersion);

      // Compare versions
      if (currentVersion) {
        console.log('[VERSION] [DIAGNOSTIC] Comparing versions:', {
          currentVersion: currentVersion.version,
          serverVersion: serverVersion.version,
          currentBuildId: currentVersion.buildId,
          serverBuildId: serverVersion.buildId
        });

        // Check version number first
        let updateAvailable = serverVersion.version !== currentVersion.version;
        console.log('[VERSION] [DIAGNOSTIC] Version numbers match?', !updateAvailable);

        // Only compare buildIds if both versions have them
        if (!updateAvailable && serverVersion.buildId && currentVersion.buildId) {
          updateAvailable = serverVersion.buildId !== currentVersion.buildId;
          console.log('[VERSION] [DIAGNOSTIC] BuildIds match?', !updateAvailable);
        }

        console.log('[VERSION] [DIAGNOSTIC] Final updateAvailable:', updateAvailable);
        setHasUpdate(updateAvailable);

        if (updateAvailable) {
          console.log('[VERSION] Update available:', {
            current: currentVersion.version,
            currentBuildId: currentVersion.buildId,
            latest: serverVersion.version,
            latestBuildId: serverVersion.buildId
          });
        } else {
          console.log('[VERSION] [DIAGNOSTIC] No update needed - versions match');
        }
      } else {
        // First time - set current version
        console.log('[VERSION] [DIAGNOSTIC] First time - storing version:', serverVersion);
        setCurrentVersion(serverVersion);
        localStorage.setItem('app_version', JSON.stringify(serverVersion));
      }
    } catch (err: any) {
      console.error('[VERSION] Error checking for updates:', err);
      setError(err.message || 'Failed to check for updates');
    } finally {
      setIsChecking(false);
    }
  };

  // Apply update (reload page to get new service worker)
  const applyUpdate = () => {
    console.log('[VERSION] [DIAGNOSTIC] applyUpdate() called');
    if (!latestVersion) {
      console.log('[VERSION] [DIAGNOSTIC] No latestVersion, aborting reload');
      return;
    }

    console.log('[VERSION] [DIAGNOSTIC] Storing new version in localStorage:', latestVersion);
    // Store new version
    localStorage.setItem('app_version', JSON.stringify(latestVersion));

    // Check if service worker is available
    if ('serviceWorker' in navigator) {
      console.log('[VERSION] [DIAGNOSTIC] Service worker supported, checking registration');
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          console.log('[VERSION] [DIAGNOSTIC] Service worker found, unregistering and reloading');
          // Unregister and reload to get fresh service worker
          registration.unregister().then(() => {
            console.log('[VERSION] [DIAGNOSTIC] *** RELOADING PAGE (after SW unregister) ***');
            window.location.reload();
          });
        } else {
          console.log('[VERSION] [DIAGNOSTIC] No service worker registration, reloading');
          // No service worker, just reload
          console.log('[VERSION] [DIAGNOSTIC] *** RELOADING PAGE (no SW) ***');
          window.location.reload();
        }
      });
    } else {
      console.log('[VERSION] [DIAGNOSTIC] Service worker not supported, reloading');
      // No service worker support, just reload
      console.log('[VERSION] [DIAGNOSTIC] *** RELOADING PAGE (no SW support) ***');
      window.location.reload();
    }
  };

  return {
    currentVersion,
    latestVersion,
    hasUpdate,
    isChecking,
    error,
    checkForUpdates,
    applyUpdate,
  };
}
